import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  ContentLayout,
  Header,
  Tabs,
  BreadcrumbGroup,
  Button,
  SpaceBetween,
  Alert,
} from '@amzn/awsui-components-react';
import { ActivitySidebar } from './sideNav';
import { publishKatalMetric } from '../katalAnalytics';
import { CoordinatorEventShiftsList } from './coordinatorEventShiftsList';
import {
  CREATE_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE,
  EVENT_CREATION,
  TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE,
} from '../../utils/constants';
import { useAttendeeSessionStorage } from '../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../hooks/attendance/participantSessionStorage';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { CoordinatorEventList } from './coordinatorEventList';
import { EventStatus } from '@amzn/red-velvet-api';
import { PAGE_KEY } from '../../hooks/attendance/urlPage';

type TabType = 'upcoming' | 'past' | 'draft';

export function CoordinatorPage() {
  const { t } = useTranslation(['activity', 'translation']);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const features = useFeatures();
  const { resetAttendees } = useAttendeeSessionStorage();
  const { resetParticipants } = useParticipantSessionStorage();

  const urlFeatures = searchParams.get('features');
  const coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const tabParam = searchParams.get('tab') as TabType | null;
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const allowedTabs: TabType[] = isEventCreationEnabled
    ? ['upcoming', 'past', 'draft']
    : ['upcoming', 'past'];
  const activeTabId: TabType = tabParam && allowedTabs.includes(tabParam) ? tabParam : 'upcoming';

  const now = useRef(new Date()).current;

  // V2 (EventCreation enabled) filters out drafts via the `status` prop; V1 omits the prop
  // to preserve its original behavior of returning the full event list.
  const upcomingTab = {
    label: t('upcoming'),
    id: 'upcoming',
    content: isEventCreationEnabled ? (
      <CoordinatorEventShiftsList
        startDate={now}
        sort="oldest-first"
        status={EventStatus.PUBLISHED}
      />
    ) : (
      <CoordinatorEventShiftsList startDate={now} sort="oldest-first" />
    ),
  };

  const pastTab = {
    label: t('past'),
    id: 'past',
    content: isEventCreationEnabled ? (
      <CoordinatorEventShiftsList
        endDate={now}
        sort="newest-first"
        status={EventStatus.PUBLISHED}
      />
    ) : (
      <CoordinatorEventShiftsList endDate={now} sort="newest-first" />
    ),
  };

  const draftTab = {
    label: t('draft'),
    id: 'draft',
    content: (
      <SpaceBetween size="s">
        <Alert type="warning">{t('draftInfoAlert')}</Alert>
        <CoordinatorEventList sort="newest-first" status={EventStatus.DRAFT} />
      </SpaceBetween>
    ),
  };

  const renderTabs = () => (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', detail.activeTabId);
        // Reset pagination when switching tabs so each tab has its own page state.
        newParams.delete(PAGE_KEY);
        setSearchParams(newParams);
      }}
      tabs={isEventCreationEnabled ? [upcomingTab, pastTab, draftTab] : [upcomingTab, pastTab]}
    />
  );

  const renderHeaderActions = () => {
    const trackUnlistedEventButton = (
      <Button
        variant={isEventCreationEnabled ? 'normal' : 'primary'}
        href={`/track-attendance${urlFeatures ? '?features=' + urlFeatures : ''}`}
        data-aci-analytics-name={TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
        onFollow={() => {
          // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
          resetAttendees();
          resetParticipants();
        }}
      >
        {t('trackUnlistedEvent')}
      </Button>
    );

    if (!isEventCreationEnabled) {
      return trackUnlistedEventButton;
    }

    return (
      <SpaceBetween size="s" direction="horizontal">
        {trackUnlistedEventButton}
        <Button
          variant="primary"
          iconName="add-plus"
          href={`/create-event${urlFeatures ? '?features=' + urlFeatures : ''}`}
          data-aci-analytics-name={CREATE_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
          onFollow={() => {
            publishKatalMetric('createEvent_entryPoint', 'coordinatorActivity');
            // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
            resetAttendees();
            resetParticipants();
          }}
        >
          {t('createEvent')}
        </Button>
      </SpaceBetween>
    );
  };

  const getContent = () => {
    return (
      <div className="page-content">
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header variant="h1" actions={renderHeaderActions()}>
                {t('pageTitle.coordinatorActivity')}
              </Header>
            </div>
          }
        >
          {renderTabs()}
        </ContentLayout>
      </div>
    );
  };

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={coordinatorActivityUrl} />}
      toolsHide={true}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              { text: t('breadcrumbs.coordinatorActivity'), href: '#' },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={getContent()}
      contentType="cards"
    />
  );
}
